import {
  Autocomplete,
  AutocompleteOption,
  Icons,
  ListItemContent,
  ListItemDecorator,
  Typography,
  VariantProp,
} from '@healthinal/ui';
import { useQueryClient } from '@tanstack/react-query';
import { matchSorter } from 'match-sorter';
import { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useCreateAddress } from '../../../api/created/address.ts';
import {
  getGetAddressesQueryKey,
  useGetAddresses,
} from '../../../api/generated/address-controller/address-controller.ts';
import { AddressDto, AddressDtoAddressType } from '../../../api/generated/model';
import { CreateAddressDialog } from './CreateAddressDialog.tsx';

const CREATE_OPTION_ID = '__create_address__';

type CreateOption = { id: typeof CREATE_OPTION_ID };
type AddressOption = AddressDto | CreateOption;

const CREATE_OPTION: CreateOption = { id: CREATE_OPTION_ID };

function isCreateOption(option: AddressOption): option is CreateOption {
  return (option as CreateOption).id === CREATE_OPTION_ID;
}

function addressKey(address: AddressDto) {
  return address.id ?? [address.organization, address.address, address.city].join('|');
}

function formatAddressLabel(address: AddressDto) {
  return [address.organization, address.address, [address.zip, address.city].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ');
}

interface Props {
  value: AddressDto[];
  onChange: (value: AddressDto[]) => void;
  variant?: VariantProp;
  disabled?: boolean;
}

export function SelectAddressMulti({ value, onChange, variant, disabled = false }: Props) {
  const queryClient = useQueryClient();
  const { data: addresses, isPending } = useGetAddresses({});
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isAutocompleteOpen, setIsAutocompleteOpen] = useState(false);
  const [createdOptions, setCreatedOptions] = useState<AddressDto[]>([]);

  const createAddressMutation = useCreateAddress({
    mutation: {
      onError: () => toast.error('Institution konnte nicht erstellt werden'),
    },
  });

  const mergedOptions = useMemo(() => {
    const byKey = new Map<string, AddressDto>();
    [...(addresses ?? []), ...createdOptions, ...value].forEach((address) => {
      byKey.set(addressKey(address), address);
    });
    return Array.from(byKey.values());
  }, [addresses, createdOptions, value]);

  const options = useMemo<AddressOption[]>(() => [...mergedOptions, CREATE_OPTION], [mergedOptions]);

  // The listbox stays open while picking institutions, so it has to be closed explicitly before the
  // create dialog opens — otherwise the popper hangs around on top of the modal.
  const openCreateDialog = () => {
    setIsAutocompleteOpen(false);
    (document.activeElement as HTMLElement | null)?.blur();
    setIsCreateDialogOpen(true);
  };

  const handleCreatedAddress = async (addressDto?: AddressDto) => {
    setIsCreateDialogOpen(false);
    if (!addressDto) return;

    try {
      const addressId = await createAddressMutation.mutateAsync({ data: addressDto });
      const created = { ...addressDto, id: addressId };
      setCreatedOptions((prev) => [...prev, created]);
      onChange([...value, created]);
      await queryClient.invalidateQueries({ queryKey: getGetAddressesQueryKey({}) });
    } catch {
      // Error toast is already handled by the mutation.
    }
  };

  return (
    <>
      <Autocomplete
        multiple
        disabled={disabled}
        loading={isPending || createAddressMutation.isPending}
        value={value}
        options={options}
        variant={variant}
        open={isAutocompleteOpen}
        onOpen={() => setIsAutocompleteOpen(true)}
        onClose={() => setIsAutocompleteOpen(false)}
        openOnFocus
        disableCloseOnSelect
        getOptionLabel={(option) =>
          isCreateOption(option) ? 'Institution erstellen' : formatAddressLabel(option)
        }
        isOptionEqualToValue={(option, selected) =>
          !isCreateOption(option) && !isCreateOption(selected) && addressKey(option) === addressKey(selected)
        }
        onChange={(_, newValue) => {
          if (newValue.some(isCreateOption)) {
            openCreateDialog();
            return;
          }
          onChange(newValue as AddressDto[]);
        }}
        filterOptions={(allOptions, { inputValue }): AddressOption[] => {
          const realOptions = allOptions.filter((option) => !isCreateOption(option)) as AddressDto[];
          const filtered = matchSorter(realOptions, inputValue, {
            keys: ['organization', 'address', 'zip', 'city'],
          });
          return [...filtered, CREATE_OPTION];
        }}
        renderOption={(props, option) =>
          isCreateOption(option) ? (
            <AutocompleteOption {...props} key={CREATE_OPTION_ID} onClick={openCreateDialog}>
              <ListItemDecorator>
                <Icons.Add />
              </ListItemDecorator>
              <Typography level="title-sm">Neue Institution erstellen</Typography>
            </AutocompleteOption>
          ) : (
            <AutocompleteOption {...props} key={addressKey(option)}>
              <ListItemContent>
                <Typography level="title-sm">{option.organization}</Typography>
                <Typography level="body-sm">
                  {option.address}
                  {option.address && ','} {option.zip} {option.city}
                </Typography>
              </ListItemContent>
            </AutocompleteOption>
          )
        }
      />

      <CreateAddressDialog
        open={isCreateDialogOpen}
        onClose={(addressDto) => void handleCreatedAddress(addressDto ?? undefined)}
        selectedAddressTypes={Object.values(AddressDtoAddressType)}
        canChangeAddressType
      />
    </>
  );
}
